import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../shared/ss-select-tooltip.directive';

import { DvmBuilderStepBase, TileRecomputeResult } from '../../dvm-layout-builder/dvm-builder-host';
import type { DashboardTile } from '../../dvm-layout-builder/dvm-layout-builder.types';
import { resolveSelfServiceApiUrl } from '../../shared/self-service-api.utils';
import type { LabelValueOption } from '../../shared/self-service-config.types';
import { SelfServiceDatasetService } from '../../shared/self-service-dataset.service';
import { resolveRecomputeChartType } from '../../shared/self-service-recompute.utils';
import { SelfServiceUiService } from '../../shared/self-service-ui.service';
import {
  DropdownOptionOrderMode,
  applyDropdownStyleKeys,
  distinctDropdownOptions,
  EMPTY_FILTER_OPERATORS,
  STANDARD_FILTER_OPERATORS,
  withEmptyFilterOperators,
  dropdownOptionsFromComputeResponse,
  normalizeDropdownOptionOrderMode,
  orderDropdownOptions,
  withStaticOptions,
} from './dropdown-options.utils';

/** Option-list filter operators that need no value. */
const EMPTY_OPERATORS = EMPTY_FILTER_OPERATORS;

/** Two selections hold the same values (order ignored). */
function sameSelection(a: readonly unknown[], b: readonly unknown[]): boolean {
  const left = [...a].map(String).sort();
  const right = [...b].map(String).sort();
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

export interface DropdownOptionGroup {
  label: string;
  displayLabel: string;
  children: string[];
  showAll: boolean;
}

/**
 * Dropdown build form (DROPDOWN): the distinct values of one dataset field
 * become the options; the picked value(s) filter the chosen targets. Renders
 * as a classic select or as always-visible buttons / tiles (with optional
 * submenu groups).
 */
@Component({
  selector: 'app-dropdown-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, NgSelectModule, SsSelectTooltipDirective],
  templateUrl: './dropdown-step.component.html',
  styles: [':host { display: contents; }'],
})
export class DropdownStepComponent extends DvmBuilderStepBase {
  readonly layoutTypes = ['DROPDOWN'] as const;
  private readonly datasetService = inject(SelfServiceDatasetService);
  private readonly ui = inject(SelfServiceUiService);

  dropdownField = '';
  dropdownDisplayLabel = '';
  dropdownIsMultiSelect = false;
  dropdownShowAllOption = false;
  dropdownShowFirstValue = false;
  dropdownFirstValueIds: string[] = [];
  /** Incoming navigation filter that selects this dropdown: '' = same column, '__off__' = never. */
  dropdownNavigationKey = '';
  dropdownDefaultValue = '';
  /** Fixed comma-separated options; when set they replace the field's dataset values. */
  dropdownStaticOptions = '';
  dropdownOptionOrderMode: DropdownOptionOrderMode = 'response';
  dropdownCustomOptionOrder = '';
  dropdownAdvancedAccordionOpen = false;
  /** Default filter on this dropdown's OWN option list (as a table column's): blank value = none. */
  dropdownFilterField = '';
  dropdownFilterOperator = 'eq';
  dropdownFilterValue = '';
  dropdownDisplayMode = 'dropdown';
  dropdownGroups: DropdownOptionGroup[] = [];
  /** ng-select `addTag` — lets the user type a value that isn't in the dataset yet. */
  dropdownGroupTag = (term: string) => term.trim();

  private dropdownDisplayModesCache: LabelValueOption[] = [];
  private dropdownDisplayModesKey: unknown = undefined;
  get dropdownDisplayModes(): LabelValueOption[] {
    const fromConfig = (this.host.layout.options as Record<string, unknown> | undefined)?.['dropdownDisplayModes'] as LabelValueOption[] | undefined;
    const key = Array.isArray(fromConfig) && fromConfig.length ? fromConfig : 'fallback';
    if (key !== this.dropdownDisplayModesKey) {
      this.dropdownDisplayModesCache = key === 'fallback'
        ? [
            { label: 'Dropdown', value: 'dropdown' },
            { label: 'Buttons', value: 'buttons' },
            { label: 'Tiles', value: 'tiles' },
          ]
        : (fromConfig as LabelValueOption[]);
      this.dropdownDisplayModesKey = key;
    }
    return this.dropdownDisplayModesCache;
  }

  get dropdownGroupShowAllDefault(): boolean {
    return (this.host.layout.options as Record<string, unknown> | undefined)?.['dropdownGroupShowAllDefault'] !== false;
  }

  addDropdownGroup(): void {
    this.dropdownGroups = [...this.dropdownGroups, { label: '', displayLabel: '', children: [], showAll: this.dropdownGroupShowAllDefault }];
  }

  removeDropdownGroup(index: number): void {
    this.dropdownGroups = this.dropdownGroups.filter((_, i) => i !== index);
  }

  /** "Fill from the screen that opened this one" choices; memoised so ng-select keeps one array. */
  get navigationKeyOptions(): Array<{ label: string; value: string }> {
    const columns = (this.host.drillDatasetColumns ?? []) as unknown[];
    const names = columns.map((column) => typeof column === 'string' ? column
      : String((column as Record<string, unknown>)?.['value'] ?? (column as Record<string, unknown>)?.['label'] ?? '')).filter(Boolean);
    if (this.dropdownNavigationKey && !['__off__'].includes(this.dropdownNavigationKey) && !names.includes(this.dropdownNavigationKey)) {
      names.push(this.dropdownNavigationKey);
    }
    const key = names.join('|');
    if (key !== this.navigationKeyOptionsKey) {
      this.navigationKeyOptionsKey = key;
      this.navigationKeyOptionsCache = [
        { label: 'Automatic — same column as this dropdown', value: '' },
        { label: 'Off — never fill from another screen', value: '__off__' },
        ...names.map((name) => ({ label: `Filter "${name}"`, value: name })),
      ];
    }
    return this.navigationKeyOptionsCache;
  }
  private navigationKeyOptionsCache: Array<{ label: string; value: string }> = [];
  /** A filter name typed in (the sending screen calls the column differently). */
  readonly addNavigationKey = (term: string) => ({ label: `Filter "${term.trim()}"`, value: term.trim() });
  private navigationKeyOptionsKey = '\u0000';

  private dropdownFieldOptionsCache: string[] = [];
  private dropdownFieldOptionsKey = '';
  /** Distinct values of the option field in the cached dataset rows — the pick list for submenu groups. */
  get dropdownFieldOptions(): string[] {
    const rows = this.host.cachedRows(this.host.drillDataset) || [];
    const key = `${this.host.drillDataset}|${this.dropdownField}|${rows.length}`;
    if (key !== this.dropdownFieldOptionsKey) {
      const seen = new Set<string>();
      if (this.dropdownField) {
        rows.forEach((row) => {
          const value = row?.[this.dropdownField];
          if (value !== null && value !== undefined && String(value).trim() !== '') seen.add(String(value));
        });
      }
      this.dropdownFieldOptionsCache = [...seen];
      this.dropdownFieldOptionsKey = key;
    }
    return this.dropdownFieldOptionsCache;
  }

  private sanitizedDropdownGroups(): DropdownOptionGroup[] {
    const checkboxExposed = this.host.fieldEnabled('dropdown', 'groupShowAll');
    return this.dropdownGroups
      .map((group) => ({
        label: String(group?.label ?? '').trim(),
        displayLabel: String(group?.displayLabel ?? '').trim(),
        children: Array.isArray(group?.children)
          ? group.children.map((child) => String(child).trim()).filter(Boolean)
          : [],
        showAll: checkboxExposed ? group?.showAll !== false : this.dropdownGroupShowAllDefault,
      }))
      .filter((group) => group.label && group.children.length);
  }

  private staticOptionList(): string[] {
    return [...new Set(this.dropdownStaticOptions.split(',').map((value) => value.trim()).filter(Boolean))];
  }

  /**
   * Operators for the option-list filter: the list a table column's default filter
   * uses (same config key), plus "is (not) empty", which need no value.
   */
  get dropdownFilterOperators(): LabelValueOption[] {
    const base = ((this.host.layout.options as Record<string, unknown> | undefined)?.['tmInitialFilterOperators'] as LabelValueOption[] | undefined) ?? STANDARD_FILTER_OPERATORS;
    const key = base.map((option) => option.value).join('|');
    if (key !== this.dropdownFilterOperatorsKey) {
      this.dropdownFilterOperatorsKey = key;
      this.dropdownFilterOperatorsCache = withEmptyFilterOperators(base);
    }
    return this.dropdownFilterOperatorsCache;
  }
  private dropdownFilterOperatorsCache: LabelValueOption[] = [];
  private dropdownFilterOperatorsKey = '\u0000';

  /** Whether the chosen operator needs a value ("is (not) empty" does not). */
  get dropdownFilterNeedsValue(): boolean {
    return !EMPTY_OPERATORS.some((option) => option.value === this.dropdownFilterOperator);
  }

  /** A column typed in - e.g. a value inside a JSON column, "approval_history.status". */
  readonly addFilterColumn = (term: string) => term.trim();

  /**
   * The filter in a neutral form: an operator of eq / neq / contains / in /
   * empty / not_empty and its value. "null" typed with Equals / Not equals
   * means empty / not empty. Null when no filter is set.
   */
  private optionFilter(): { field: string; operator: string; value: string } | null {
    const field = this.dropdownFilterField.trim();
    if (!field) return null;
    const raw = this.dropdownFilterValue.trim();
    let operator = this.dropdownFilterOperator || 'eq';
    if (raw.toLowerCase() === 'null' && (operator === 'eq' || operator === 'neq')) operator = operator === 'eq' ? 'empty' : 'not_empty';
    if (operator !== 'empty' && operator !== 'not_empty' && !raw) return null;
    return { field, operator, value: raw };
  }

  /** The same filter for the builder's distinct-value lookup (getData `filterList`). */
  private optionFilterList(): Array<Record<string, unknown>> {
    const filter = this.optionFilter();
    // A value inside a JSON column is only understood by the compute API.
    if (!filter || filter.field.includes('.')) return [];
    const base = { attributeName: filter.field, logicalOperator: 'AND' };
    switch (filter.operator) {
      case 'not_empty': return [{ ...base, operator: 'IS NOT', attributeValue: null }, { ...base, operator: '!=', attributeValue: '' }];
      case 'empty': return [{ ...base, operator: 'IS', attributeValue: null }];
      case 'neq': return [{ ...base, operator: '!=', attributeValue: filter.value }];
      case 'contains': return [{ ...base, operator: 'LIKE', attributeValue: `%${filter.value}%` }];
      case 'in': return [{ ...base, operator: 'IN', attributeValue: filter.value.split(',').map((value) => value.trim()).filter(Boolean) }];
      default: return [{ ...base, operator: '=', attributeValue: filter.value }];
    }
  }

  /**
   * The option-list filter as the backend reads it (`optionFilters`): applied
   * only when this dropdown's options are listed, never to its targets.
   * Kept in an ARRAY so publishing (which strips nested `value` keys) keeps it.
   */
  private optionFiltersPayload(): Array<Record<string, unknown>> {
    const filter = this.optionFilter();
    if (!filter) return [];
    const { field, value } = filter;
    const dataset = this.host.drillDataset;
    switch (filter.operator) {
      case 'not_empty': return [{ dataset, field, operator: 'NOT_EMPTY', value: null }];
      case 'empty': return [{ dataset, field, operator: 'EMPTY', value: null }];
      case 'in': return [{ dataset, field, operator: 'IN', value: value.split(',').map((part) => part.trim()).filter(Boolean) }];
      case 'contains': return [{ dataset, field, operator: 'LIKE', value: `%${value}%` }];
      default: return [{ dataset, field, operator: filter.operator === 'neq' ? '!=' : '=', value }];
    }
  }

  get dropdownOptionOrderModes(): LabelValueOption[] {
    const fromConfig = (this.host.layout.options as Record<string, unknown> | undefined)?.['dropdownOptionOrderModes'] as LabelValueOption[] | undefined;
    return Array.isArray(fromConfig) && fromConfig.length
      ? fromConfig
      : [
          { label: 'Backend response order', value: 'response' },
          { label: 'Ascending', value: 'asc' },
          { label: 'Descending', value: 'desc' },
          { label: 'Custom', value: 'custom' },
        ];
  }

  onDropdownFieldDrop(event: DragEvent): void {
    const mapping = this.host.readFieldDrop(event);
    if (!mapping) return;
    this.host.useDroppedSingleDataset(mapping.dataset);
    this.dropdownField = mapping.field;
    if (!this.dropdownDisplayLabel) this.dropdownDisplayLabel = mapping.field;
    this.host.invalidatePreview();
  }

  /** The DROPDOWN-scoped values of the shared Format accordion (pill colours / radius / font). */
  dropdownFormatFromAccordion(): Record<string, unknown> {
    const format: Record<string, unknown> = {};
    this.host.applicableFormatControls('DROPDOWN').forEach((control) => {
      const value = this.host.barFormat[control.key];
      if (value !== undefined) format[control.key] = value;
    });
    return format;
  }

  // ── DvmBuilderStep ──────────────────────────────────────────────────────

  blockers(): string[] {
    if (!this.host.selectedDatasets.length) return ['Dataset'];
    const missing: string[] = [];
    if (!this.dropdownField) missing.push('Field');
    if (!this.host.selectedTargetCharts.length) missing.push('At least one target chart');
    return missing;
  }

  buildPayload(): Record<string, unknown> {
    const defaultValue = this.dropdownDefaultValue.trim()
      ? this.dropdownDefaultValue.split(',').map((value) => value.trim()).filter(Boolean)
      : [];
    return {
      componentId: this.host.componentId,
      dashboardId: this.host.dashboardId,
      componentType: 'INPUT',
      inputType: 'DROPDOWN',
      title: this.host.title.trim(),
      dataset: this.host.drillDataset,
      field: this.dropdownField,
      displayLabel: this.dropdownDisplayLabel.trim() || this.host.title.trim(),
      isMultiSelect: this.dropdownIsMultiSelect,
      showAllOption: this.dropdownShowAllOption,
      ...(this.host.supportsDropdownFirstValue ? {
        showFirstValue: this.dropdownShowFirstValue,
        firstValueDropdownIds: this.dropdownShowFirstValue ? [...this.dropdownFirstValueIds] : [],
      } : {}),
      ...(this.dropdownNavigationKey ? { navigationFilterKey: this.dropdownNavigationKey } : {}),
      displayMode: this.dropdownDisplayMode || 'dropdown',
      optionGroups: this.sanitizedDropdownGroups(),
      optionOrderMode: this.dropdownOptionOrderMode,
      customOptionOrder: this.dropdownCustomOptionOrder.trim(),
      ...(this.staticOptionList().length ? { staticOptions: this.staticOptionList() } : {}),
      // The builder's own settings (restored by loadFromTile) and the filter the backend applies.
      optionFilterField: this.dropdownFilterField.trim(),
      optionFilterOperator: this.dropdownFilterOperator || 'eq',
      optionFilterValue: this.dropdownFilterValue.trim(),
      optionFilters: this.optionFiltersPayload(),
      capabilities: this.dropdownFormatFromAccordion(),
      defaultValue: this.dropdownIsMultiSelect ? defaultValue : (defaultValue[0] ?? ''),
      value: this.dropdownIsMultiSelect ? defaultValue : (defaultValue.length ? [defaultValue[0]] : []),
      targets: this.host.selectedTargetCharts.map((componentId) => ({
        componentId,
        label: this.host.targetLabelForId(componentId),
        field: this.dropdownField,
      })),
      ...this.host.apiCallConfigPayload(),
    };
  }

  loadFromTile(tile: DashboardTile, payload: Record<string, unknown>): void {
    this.dropdownField = String(payload['field'] ?? '');
    this.dropdownDisplayLabel = String(payload['displayLabel'] ?? '');
    this.dropdownIsMultiSelect = !!payload['isMultiSelect'];
    this.dropdownShowAllOption = payload['showAllOption'] === true;
    this.dropdownShowFirstValue = payload['showFirstValue'] === true;
    this.dropdownFirstValueIds = Array.isArray(payload['firstValueDropdownIds'])
      ? payload['firstValueDropdownIds'].filter((id): id is string => typeof id === 'string') : [];
    this.dropdownNavigationKey = typeof payload['navigationFilterKey'] === 'string' ? payload['navigationFilterKey'] : '';
    this.dropdownDisplayMode = String(payload['displayMode'] ?? 'dropdown');
    const savedGroups = Array.isArray(payload['optionGroups'])
      ? payload['optionGroups']
      : tile.dropdownControl?.widgetData?.['optionGroups'];
    this.dropdownGroups = Array.isArray(savedGroups)
      ? (savedGroups as Array<Record<string, unknown>>).map((group) => ({
          label: String(group?.['label'] ?? ''),
          displayLabel: String(group?.['displayLabel'] ?? ''),
          children: Array.isArray(group?.['children']) ? (group['children'] as unknown[]).map(String) : [],
          showAll: group?.['showAll'] !== false,
        }))
      : [];
    this.dropdownOptionOrderMode = normalizeDropdownOptionOrderMode(payload['optionOrderMode'] ?? tile.dropdownControl?.widgetData?.['optionOrderMode']);
    this.dropdownCustomOptionOrder = String(payload['customOptionOrder'] ?? tile.dropdownControl?.widgetData?.['customOptionOrder'] ?? '');
    this.dropdownAdvancedAccordionOpen = this.dropdownOptionOrderMode !== 'response';
    this.dropdownStaticOptions = Array.isArray(payload['staticOptions']) ? (payload['staticOptions'] as unknown[]).map(String).join(', ') : '';
    this.dropdownFilterField = String(payload['optionFilterField'] ?? '');
    this.dropdownFilterOperator = String(payload['optionFilterOperator'] ?? 'eq') || 'eq';
    this.dropdownFilterValue = String(payload['optionFilterValue'] ?? '');
    const rawDefault = payload['defaultValue'];
    this.dropdownDefaultValue = Array.isArray(rawDefault) ? rawDefault.map(String).join(', ') : String(rawDefault ?? '');
    const targets = Array.isArray(payload['targets']) ? payload['targets'] as Array<Record<string, unknown>> : [];
    this.host.selectedTargetCharts = targets.map((target) => String(target['componentId'] ?? '')).filter(Boolean);
    this.host.rehydrateOwnFormulaReferences(this.host.componentId, payload['references']);
    const savedTileFormat = {
      ...((payload['capabilities'] as Record<string, unknown>) || {}),
      ...((tile.format || {}) as Record<string, unknown>),
    };
    this.host.applicableFormatControls('DROPDOWN').forEach((control) => {
      if (savedTileFormat[control.key] !== undefined) this.host.barFormat[control.key] = savedTileFormat[control.key];
    });
    if (this.host.drillDataset) this.host.loadColumnsForDataset(this.host.drillDataset);
  }

  reset(): void {
    this.dropdownField = '';
    this.dropdownDisplayLabel = '';
    this.dropdownIsMultiSelect = false;
    this.dropdownShowAllOption = false;
    this.dropdownShowFirstValue = false;
    this.dropdownFirstValueIds = [];
    this.dropdownNavigationKey = '';
    this.dropdownDefaultValue = '';
    this.dropdownStaticOptions = '';
    this.dropdownDisplayMode = 'dropdown';
    this.dropdownGroups = [];
    this.dropdownOptionOrderMode = 'response';
    this.dropdownCustomOptionOrder = '';
    this.dropdownAdvancedAccordionOpen = false;
    this.dropdownFilterField = '';
    this.dropdownFilterOperator = 'eq';
    this.dropdownFilterValue = '';
  }

  /** Format accordion changed: fold the DROPDOWN-scoped values into the tile being edited. */
  onFormatChanged(): void {
    if (!this.host.editingTileId) return;
    const index = this.host.dashboardTiles.findIndex((t) => t.id === this.host.editingTileId);
    const tile = index >= 0 ? this.host.dashboardTiles[index] : null;
    if (!tile || tile.layoutType !== 'DROPDOWN') return;
    tile.format = { ...(tile.format || {}), ...this.dropdownFormatFromAccordion() };
    this.host.onTileFormatChange(tile);
    this.host.dashboardTiles = this.host.dashboardTiles.map((t, i) => (i === index ? { ...tile } : t));
  }

  /** Loads the distinct option values, adds the tile, then pushes its (default) value to the targets. */
  generate(payload: Record<string, unknown>): boolean {
    const filterApiUrl = resolveSelfServiceApiUrl(this.host.layout.apiEndpoints?.filterApi, '/getData');
    // The option-list filter applies here too, so the builder shows the same options as the published view.
    this.datasetService.getDistinctFieldValues(filterApiUrl, this.host.drillDataset, this.dropdownField, this.optionFilterList()).subscribe({
      next: (rows) => this.addGeneratedTile(payload, withStaticOptions(this.staticOptionList(), distinctDropdownOptions(rows, this.dropdownField))),
      error: (error: HttpErrorResponse) => {
        this.host.isGenerating = false;
        this.host.generateError = error.error?.message || error.message || 'Unable to load dropdown option values.';
        this.ui.showError(this.host.generateError);
      },
    });
    return true;
  }

  private addGeneratedTile(payload: Record<string, unknown>, options: string[]): void {
    this.host.isGenerating = false;
    const dropdownTile = this.buildTile(payload, options);
    // The selection the targets are filtered by right now (before the rebuild).
    const before = this.host.dashboardTiles.find((tile) => tile.id === dropdownTile.id)?.dropdownControl?.value ?? [];
    const updated = this.host.addTile(dropdownTile);
    this.ui.showSuccess(updated ? 'Dropdown updated on the dashboard.' : 'Dropdown added to the dashboard.');
    const persisted = this.host.dashboardTiles.find((tile) => tile.id === dropdownTile.id) ?? dropdownTile;
    const value = persisted.dropdownControl?.value ?? [];
    // Only a CHANGED selection reaches the targets: re-saving the dropdown's
    // settings (its option filter, order, label ...) must not recompute the
    // charts / tables it filters.
    if (!sameSelection(before, value)) this.host.onTileDropdownChange(persisted, value, payload);
    // Same selection: only its own option list reloads (with its new option filter).
    else this.host.reloadDropdownOptions?.(persisted);
  }

  private buildTile(payload: Record<string, unknown>, options: string[]): DashboardTile {
    const base = this.host.newTileBase(payload);
    const orderedOptions = orderDropdownOptions(options, this.dropdownOptionOrderMode, this.dropdownCustomOptionOrder);
    const defaultValues = this.dropdownDefaultValue.trim()
      ? this.dropdownDefaultValue.split(',').map((value) => value.trim()).filter((value) => orderedOptions.includes(value))
      : [];
    const value = this.dropdownIsMultiSelect ? defaultValues : defaultValues.slice(0, 1);
    const isButtonMode = this.dropdownDisplayMode === 'buttons' || this.dropdownDisplayMode === 'tiles';
    const dropdownFormat = this.dropdownFormatFromAccordion();
    const widgetData: Record<string, unknown> = {
      name: this.dropdownDisplayLabel.trim() || this.host.title.trim() || this.dropdownField,
      key: this.dropdownField,
      isMultiSelect: this.dropdownIsMultiSelect,
      showAllOption: this.dropdownShowAllOption,
      // The widget reads it to decide whether its value can be cleared (×).
      showFirstValue: !!this.host.supportsDropdownFirstValue && this.dropdownShowFirstValue,
      displayMode: this.dropdownDisplayMode || 'dropdown',
      optionGroups: this.sanitizedDropdownGroups(),
      optionOrderMode: this.dropdownOptionOrderMode,
      customOptionOrder: this.dropdownCustomOptionOrder.trim(),
      textCase: 'capital',
      optionsList: orderedOptions,
    };
    applyDropdownStyleKeys(dropdownFormat, widgetData);
    return {
      ...base,
      payload: { ...payload, value },
      colSpan: 4,
      rowSpan: isButtonMode ? 5 : 3,
      format: dropdownFormat,
      dropdownControl: {
        field: this.dropdownField,
        options: orderedOptions,
        value,
        targets: [...this.host.selectedTargetCharts],
        widgetData,
        apiConfig: { ...this.host.buildApiCallConfig },
      },
    };
  }

  // ── Tile-side hooks (DvmBuilderStep) ──────────────────────────────────────

  applyFormatToTile(tile: DashboardTile, format: Record<string, unknown>): void {
    if (!tile.dropdownControl) return;
    const widgetData: Record<string, unknown> = { ...(tile.dropdownControl.widgetData || {}) };
    applyDropdownStyleKeys(format, widgetData);
    tile.dropdownControl = { ...tile.dropdownControl, widgetData };
    const capabilities: Record<string, unknown> = {};
    applyDropdownStyleKeys(format, capabilities);
    tile.payload = { ...tile.payload, capabilities };
  }

  /** Only an INPUT-typed result feeds a dropdown: its rows become the options (keeping still-valid selections). */
  applyRecomputeResult(tile: DashboardTile, { entry, result }: TileRecomputeResult): boolean {
    if (resolveRecomputeChartType(entry) !== 'INPUT' || !tile.dropdownControl) return false;
    const rawOptions = Array.isArray(result['data']) ? (result['data'] as unknown[]) : [];
    const legacy = rawOptions
      .map((item) => (item && typeof item === 'object' ? String((item as Record<string, unknown>)['value'] ?? '') : String(item ?? '')))
      .filter(Boolean);
    // Rows keyed by the column ({ cust_type: 'SANY' }) or an options list: read
    // them like the rest of the app does. Reading only `value` gave an empty
    // list, which then removed the dropdown's selected value.
    const serverOptions = legacy.length ? legacy : dropdownOptionsFromComputeResponse(result, tile.dropdownControl.field);
    // No option data in this answer at all: keep what the dropdown has.
    if (!serverOptions.length) return false;
    const staticOptions = Array.isArray(tile.payload?.['staticOptions']) ? (tile.payload['staticOptions'] as unknown[]).map(String) : [];
    const options = withStaticOptions(staticOptions, serverOptions);
    // A dropdown from the published design may have no `value` yet (its options not loaded).
    const value = (tile.dropdownControl.value ?? []).filter((v) => options.includes(v));
    tile.payload = { ...tile.payload, value };
    tile.dropdownControl = { ...tile.dropdownControl, options, value };
    return true;
  }

  /** Tooltip of the "+N more" chip: the names it stands for. */
  moreTargets(items: Array<{ label?: string; value?: unknown }>): string {
    return items.slice(3).map((item) => item.label || String(item.value ?? '')).join(', ');
  }
}
